import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown as BootstrapDropdown } from 'bootstrap';
import { useClickDetector } from '@hooks/useClickDetector';
import styles from './Dropdown.module.css';

interface DropdownProps {
    buttonContent: JSX.Element;
    buttonAriaLabel: string;
    position?: 'left' | 'center' | 'right';
    menuItems?: JSX.Element[];
    withToggle?: boolean;
    fullWidthOnMobile?: boolean;
    children?: JSX.Element;
}

export const Dropdown: FC<DropdownProps> = ({ buttonContent, buttonAriaLabel, position, menuItems, withToggle, fullWidthOnMobile, children }) => {
    const [instance, setInstance] = useState<BootstrapDropdown | null>(null);
    const [show, setShow] = useState<boolean>(false);

    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // destroy the dropdown when the component unmounts
        return () => {
            try {
                instance?.dispose();
            } catch (e) {
                console.log('catch error disposing of dropdown');
            }
        };
    }, [instance]);

    const onRefChange = useCallback((node: HTMLDivElement) => {
        if (node !== null) {
            const newInstance = BootstrapDropdown.getOrCreateInstance(node, { display: 'static' });
            setInstance(newInstance);
        }
    }, []);

    useEffect(() => {
        show ? instance?.show() : instance?.hide();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    useClickDetector([{ elemRef: dropdownRef, onClickOutside: () => setShow(false) }]);

    return (
        <div
            ref={dropdownRef}
            className={`d-flex dropdown ${styles.dropdown} ${fullWidthOnMobile ? styles['full-width-mobile'] : ''}`}>
            <button
                className={`d-block link-body-emphasis text-decoration-none ${withToggle ? styles['dropdown-toggle'] : ''}`}
                onClick={() => setShow(!show)}
                aria-label={buttonAriaLabel}>
                {buttonContent}
            </button>
            <div
                ref={onRefChange}
                className={
                    `dropdown-menu ${styles['dropdown-menu']} p-0` +
                    // positioning
                    ` ${position === 'right' ? 'dropdown-menu-end' : position === 'center' ? styles['dropdown-menu-center'] : ''}`
                }>
                {children}
                {menuItems && (
                    <ul className="m-0 p-3">
                        {menuItems?.map((item, index) => (
                            <li key={`item-${index}`} className="dropdown-item" onClick={() => setShow(false)}>
                                {item}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};
